/**
 * recommendations — «Сильные стороны и рекомендации»: список сильных сторон
 * и список улучшений (с приоритетом, категорией и шагами действий). Зеркалит
 * RecommendationsSection.tsx: тот же гейт (обе пустые → секции нет), тот же
 * порядок (сильные стороны → улучшения), приоритет печатается СЫРОЙ строкой
 * без перевода — экран (`<Tag>{imp.priority}</Tag>`) тоже не переводит
 * 'high'/'medium'/'low', хотя ключи `common.high`/`medium`/`low` существуют;
 * заводить отдельный i18n-путь для этого текста не пришлось.
 *
 * Заголовки блоков — легаси-ключи `pdf.sections.strengths` /
 * `pdf.sections.recommendations` (переживают старый printReport.ts, задача
 * 12 удалит только сам рендерер, не эти строки): их текст на всех 4 локалях
 * уже совпадает с согласованным прототипом (preview.html:263,267) буквально.
 *
 * Счётчик в шапке карточки (RecommendationsSection.tsx показывает суммарное
 * число strengths+improvements) сознательно не печатается: согласованный
 * прототип (preview.html:259) карточку без счётчика в шапке рисует, а бриф
 * задачи 8 количество не упоминает вовсе.
 *
 * Раскладка `.rec`: жирная строка «criterionKey. + приоритет» отдельно от
 * абзаца с advice — вёрстка прототипа (preview.html:268), а не однострочный
 * flex экрана; данные те же (criterionKey, advice, priority), меняется
 * только расположение — тот же класс расхождений, что и в dialogue.ts.
 * Без вывода и приоритета строки заголовка нет, без совета — абзаца (#1423).
 */
import { recommendationParts } from '@aica/core';
import { card, block, tag } from '../primitives';
import { escapeHtml } from '../escape';
import type { SectionFn } from '../types';

const PRIORITY_VARIANT = { high: 'bad', medium: 'warn', low: 'muted' } as const;

export const renderRecommendations: SectionFn = (input, t) => {
  const { recommendations } = input.analysis.result;
  const strengths = recommendations?.strengths ?? [];
  const improvements = recommendations?.improvements ?? [];
  if (strengths.length === 0 && improvements.length === 0) return '';

  const strengthsHtml =
    strengths.length > 0
      ? block(
          t('pdf.sections.strengths'),
          `<ul>${strengths.map((s) => `<li>${escapeHtml(s)}</li>`).join('')}</ul>`
        )
      : '';

  const improvementsHtml =
    improvements.length > 0
      ? block(
          t('pdf.sections.recommendations'),
          improvements
            .map((imp) => {
              const priorityTag = imp.priority
                ? tag(imp.priority, PRIORITY_VARIANT[imp.priority])
                : '';
              const categoryHtml = imp.category
                ? `<p style="font-size:10px;color:var(--fg3)">${escapeHtml(imp.category)}</p>`
                : '';
              const actionItemsHtml =
                imp.actionItems && imp.actionItems.length > 0
                  ? `<ul>${imp.actionItems
                      .map(
                        (ai) =>
                          `<li>${escapeHtml(ai.step)}${
                            ai.immediateAction
                              ? ` <span style="color:var(--fair)">→ ${escapeHtml(ai.immediateAction)}</span>`
                              : ''
                          }</li>`
                      )
                      .join('')}</ul>`
                  : '';
              const parts = recommendationParts(imp.criterionKey, imp.advice);
              // No lead, no heading: the advice stands alone (#1423).
              const leadHtml = parts.lead ? `<b>${escapeHtml(parts.lead)}</b>` : '';
              const headHtml =
                leadHtml || priorityTag
                  ? `<div class="r1">${leadHtml}${priorityTag}</div>`
                  : '';
              return (
                `<div class="rec">${headHtml}` +
                `${parts.advice ? `<p>${escapeHtml(parts.advice)}</p>` : ''}${categoryHtml}${actionItemsHtml}</div>`
              );
            })
            .join('')
        )
      : '';

  return card({
    title: t('records.analysisPanel.strengthsRecommendations'),
    body: strengthsHtml + improvementsHtml,
  });
};
